import * as LucideIcons from "lucide-react";
import { LucideIcon, LucideProps } from "lucide-react";
import { ForwardRefExoticComponent, RefAttributes } from "react";
import { logIconMapping } from "./consoleLogger";

// Define a more precise type for Lucide icons
type IconComponent = ForwardRefExoticComponent<Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>>;

// Create a case-insensitive map for icon lookup
const createCaseInsensitiveIconMap = () => {
  const iconMap: Record<string, LucideIcon> = {};
  
  // Enable debug mode
  const debug = true;
  
  if (debug) {
    logIconMapping("[ICONS] Creating icon mapping");
    
    // Log all available Lucide icons for debugging
    logIconMapping("[ICONS] Available in LucideIcons:", 
      Object.keys(LucideIcons)
        .filter(key => typeof LucideIcons[key as keyof typeof LucideIcons] === "function")
        .slice(0, 20)
    );
  }
  
  Object.entries(LucideIcons)
    .filter(([key, value]) => {
      // Less restrictive filter that only excludes non-components
      const isValid = (
        typeof value === "function" &&
        key !== "createLucideIcon" &&
        key !== "icons" &&
        !key.startsWith("__")
      );
      
      if (debug && !isValid && ['Baby', 'Home', 'Activity'].includes(key)) {
        logIconMapping(`[ICONS] Filtered out important icon: ${key}`, value);
      }
      
      return isValid;
    })
    .forEach(([key, value]) => {
      // Add both the original key and lowercase version for case-insensitive lookup
      iconMap[key] = value as LucideIcon;
      iconMap[key.toLowerCase()] = value as LucideIcon;
      
      if (debug && ['Baby', 'Home', 'Activity'].includes(key)) {
        logIconMapping(`[ICONS] Added icon: ${key}`, value);
      }
    });
  
  if (debug) {
    logIconMapping(`[ICONS] Total icons mapped: ${Object.keys(iconMap).length / 2}`);
    // Log specific icons we know we need
    logIconMapping("[ICONS] Critical icons check:", {
      hasBaby: !!iconMap["Baby"],
      hasActivity: !!iconMap["Activity"],
      hasHome: !!iconMap["Home"]
    });
  }
    
  return iconMap;
};

// Type-safe icon mapping with case insensitivity
export const iconMapping = createCaseInsensitiveIconMap();

// Debug available icons
logIconMapping("[ICONS] Available icons (sample):", 
  Object.keys(iconMapping)
    .filter(k => k === k.charAt(0).toUpperCase() + k.slice(1) && k.length < 10)
    .slice(0, 20)
);

// Helper function to get an icon component by name with improved error handling
export const getIconByName = (name: string): LucideIcon | null => {
  if (!name) {
    logIconMapping("[ICONS] Empty icon name provided");
    return null;
  }
  
  logIconMapping(`[ICONS] Looking up icon: "${name}"`);
  
  // Try exact match first
  if (iconMapping[name]) {
    logIconMapping(`[ICONS] Found icon "${name}" with exact match`);
    return iconMapping[name];
  }
  
  // Try case-insensitive match
  const lowercaseName = name.toLowerCase();
  if (iconMapping[lowercaseName]) {
    logIconMapping(`[ICONS] Found icon "${name}" using case-insensitive match: ${lowercaseName}`);
    return iconMapping[lowercaseName];
  }
  
  // Try pascal case conversion (database might store "activity" but component is "Activity")
  const pascalCaseName = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  if (iconMapping[pascalCaseName]) {
    logIconMapping(`[ICONS] Found icon "${name}" using pascal case conversion: ${pascalCaseName}`);
    return iconMapping[pascalCaseName];
  }
  
  // Check if the exact name exists in LucideIcons directly
  if (name in LucideIcons) {
    logIconMapping(`[ICONS] Found icon "${name}" directly in LucideIcons`);
    const icon = LucideIcons[name as keyof typeof LucideIcons] as unknown as LucideIcon;
    // Add it to our mapping for future use
    iconMapping[name] = icon;
    iconMapping[name.toLowerCase()] = icon;
    return icon;
  }
  
  // Log failed lookup for debugging
  logIconMapping(`[ICONS] Icon not found: "${name}". Available icons include: Baby, Activity, Home, etc.`);
  
  // Last-ditch attempt: Look for any icon with a similar name
  const similarIconKey = Object.keys(iconMapping)
    .find(k => k.toLowerCase().includes(name.toLowerCase()));
    
  if (similarIconKey) {
    logIconMapping(`[ICONS] Found similar icon: "${similarIconKey}" for "${name}"`);
    return iconMapping[similarIconKey];
  }
  
  return null;
};
